import React, { useEffect, useRef, useState } from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { Button } from './ui/button';

const Pricing = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnnual, setIsAnnual] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -100px 0px'
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const pricingPlans = [
    {
      id: 1,
      name: 'Individual Protection',
      monthlyPrice: 3.74,
      description: 'Perfect for trying out our training system',
      features: [
        'Protect 1 loved one',
        '1 Monthly advanced AI training session',
        'Email support',
      ],
      popular: false,
      buttonText: 'Get Started'
    },
    {
      id: 2,
      name: 'Family Protection',
      monthlyPrice: 4.99,
      description: 'Comprehensive protection for your whole family',
      features: [
        'Protect up to 4 loved ones',
        'Monthly advanced AI training session (one per loved one)',
        'Advanced coaching analytics',
        'Family dashboard',
        'Priority support',
        'Progress tracking',
        'Custom scenarios'
      ],
      popular: true,
      buttonText: 'Best Value'
    },
    {
      id: 3,
      name: 'Enterprise',
      description: 'For organizations and care facilities',
      features: [
        'Multiple family accounts',
        'Admin dashboard',
        'Bulk training sessions',
        'Custom integrations',
        'Dedicated support',
        'Advanced reporting'
      ],
      popular: false,
      buttonText: 'Contact Sales'
    }
  ];

  return (
    <section className="py-8 bg-white relative overflow-hidden" id="pricing" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-8 relative z-10">
        <div className={`text-center mb-6 transition-all duration-800 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h2 className="text-3xl lg:text-4xl font-bold leading-tight mb-6 font-sans">
            Choose the <span className="text-blue-500 font-black">Right Plan</span> for Your Family
          </h2>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto font-sans">
            Flexible pricing options to meet your needs and budget
          </p>
        </div>

        <div className={`flex justify-center mb-8 transition-all duration-800 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '0.1s' }}>
          <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1 gap-1">
            <Button
              variant={!isAnnual ? "default" : "ghost"}
              size="sm"
              onClick={() => setIsAnnual(false)}
              className={`px-4 py-2 text-sm font-semibold ${!isAnnual ? 'bg-blue-500 text-white hover:bg-blue-600' : 'text-slate-600 hover:text-slate-900'}`}
            >
              Monthly
            </Button>
            <Button
              variant={isAnnual ? "default" : "ghost"}
              size="sm"
              onClick={() => setIsAnnual(true)}
              className={`px-4 py-2 text-sm font-semibold ${isAnnual ? 'bg-blue-500 text-white hover:bg-blue-600' : 'text-slate-600 hover:text-slate-900'}`}
            >
              Annual (Save 20%)
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8">
          {pricingPlans.map((plan, index) => (
            <div key={plan.id}>
              {/* Mobile view - entire card in accordion */}
              <div className="md:hidden">
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value={`plan-${plan.id}`} className={`border rounded-xl transition-all duration-800 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} ${plan.popular ? 'ring-2 ring-blue-500 border-blue-500' : 'border-slate-200'}`} style={{ transitionDelay: `${index * 0.1}s` }}>
                    <AccordionTrigger className="px-6 py-4 text-left hover:no-underline">
                      <div className="flex items-center justify-between w-full gap-4">
                        <div className="flex-1">
                          <h3 className="text-xl font-bold font-sans text-slate-900">{plan.name}</h3>
                          {plan.monthlyPrice !== undefined ? (
                            <div className="flex items-baseline gap-1 mt-1">
                              <span className="text-2xl font-bold font-sans text-slate-900">${isAnnual ? (plan.monthlyPrice * 0.8).toFixed(2) : plan.monthlyPrice}</span>
                              <span className="text-slate-600 font-sans text-sm">/month</span>
                            </div>
                          ) : (
                            <p className="text-slate-600 font-sans text-sm">{plan.description}</p>
                          )}
                        </div>
                        {plan.popular && (
                          <div className="bg-blue-500 text-white px-3 py-1 rounded-full text-xs font-semibold font-sans flex-shrink-0">
                            {plan.buttonText}
                          </div>
                        )}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-6 pb-6">
                      <div className="space-y-6">
                        {plan.description && (
                          <p className="text-slate-600 font-sans">{plan.description}</p>
                        )}
                        
                        {isAnnual && plan.monthlyPrice !== undefined && (
                          <p className="text-green-600 text-sm font-sans">Save ${(plan.monthlyPrice * 0.2 * 12).toFixed(0)}/year</p>
                        )}

                        <ul className="space-y-3">
                          {plan.features.map((feature, featureIndex) => (
                            <li key={featureIndex} className="flex items-start gap-3">
                              <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-3 h-3 text-white">
                                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </div>
                              <span className="text-slate-700 font-sans text-sm">{feature}</span>
                            </li>
                          ))}
                        </ul>

                        <button className={`w-full py-3 rounded-lg font-semibold transition-all duration-300 font-sans ${
                          plan.popular 
                            ? 'bg-blue-500 text-white hover:bg-blue-600' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                        }`}>
                          {plan.buttonText}
                        </button>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>

              {/* Desktop view - normal card */}
              <div
                className={`hidden md:block relative bg-white rounded-xl p-8 transition-all duration-800 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} ${plan.popular ? 'ring-2 ring-blue-500' : 'border border-slate-200'}`}
                style={{ transitionDelay: `${index * 0.1}s` }}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-blue-500 text-white px-4 py-2 rounded-full text-sm font-semibold font-sans">
                    {plan.buttonText}
                  </div>
                )}
                
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold mb-2 font-sans">{plan.name}</h3>
                  <p className="text-slate-600 mb-6 font-sans">{plan.description}</p>
                  <div className="mb-6">
                    {plan.monthlyPrice !== undefined ? (
                      <>
                        <span className="text-4xl font-bold font-sans">${isAnnual ? (plan.monthlyPrice * 0.8).toFixed(2) : plan.monthlyPrice}</span>
                        <span className="text-slate-600 font-sans">/{isAnnual ? 'month' : 'month'}</span>
                      </>
                    ) : null}
                  </div>
                  {isAnnual && plan.monthlyPrice !== undefined && (
                    <p className="text-green-600 text-sm font-sans">Save ${(plan.monthlyPrice * 0.2 * 12).toFixed(0)}/year</p>
                  )}
                </div>

                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start gap-3">
                      <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-3 h-3 text-white">
                          <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      <span className="text-slate-700 font-sans">{feature}</span>
                    </li>
                  ))}
                </ul>

                <button className={`w-full py-3 rounded-lg font-semibold transition-all duration-300 font-sans ${
                  plan.popular 
                    ? 'bg-blue-500 text-white hover:bg-blue-600' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}>
                  {plan.buttonText}
                </button>
              </div>
            </div>
          ))}
        </div>

              </div>
    </section>
  );
};

export default Pricing;
